/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Button, Grid, Box, TextField, MenuItem, Alert } from '@mui/material';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { selectHotelName } from 'modules/dashboard/selectors';
import { rateCalenderActions } from 'modules/rate-calendar/slice';
import {
  selectLoader,
  selectMajorEventCategories,
  selectMinorEventCategories,
} from 'modules/rate-calendar/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'modules/common/components';
import { dateTimeFormat } from 'modules/common/helpers/date-time';
import { isEmpty } from 'modules/common/helpers/object';
import ERROR_TYPES from 'modules/common/constants/error-types';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { addEventValidation } from '../../validations/event-validations';
/**
 * Update event view is defined here
 * @param {*} param0
 * @returns
 */
const UpdateEventForm = ({ editEvent, isEditSelected, setEditSelected }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const notification = useSelector(selectNotification);
  const hotel = useSelector(selectHotelName);
  const majorEventCategories = useSelector(selectMajorEventCategories);
  const minorEventCategories = useSelector(selectMinorEventCategories);
  const loading = useSelector(selectLoader);
  //
  const formElement = useRef(null);
  //
  const onUpdateEvent = async (values) => {
    dispatch(
      rateCalenderActions.updateEventDetails({
        ...values,
        organizationId,
        hotelId: hotel.id,
        id: editEvent.id,
      })
    );
    setEditSelected(false);
  };
  //
  const initialValues = {
    date: new Date(editEvent?.date).toLocaleString('fr-CA', dateTimeFormat) ?? new Date(),
    majorCategory: editEvent?.majorCategory,
    minorCategory: editEvent?.minorCategory,
    description: editEvent?.description,
  };
  //
  useEffect(() => {
    const query = `limit=${PAGINATION.MINOR_EVENT_CATEGORIES_PER_PAGE}`;
    const majorEventCategoryId = majorEventCategories?.filter(
      (filter) => filter?.name === editEvent?.majorCategory
    )[0]?.id;
    if (majorEventCategoryId) {
      dispatch(
        rateCalenderActions.getMinorEventCategories({
          organizationId,
          majorEventCategoryId,
          query,
        })
      );
    }
  }, [majorEventCategories, editEvent]);
  //
  return (
    <Loader loading={loading}>
      <Grid item container direction="column" sx={{ flexGrow: 1, flexBasis: 1 }}>
        <Box
          xs={12}
          md={3}
          mx={2}
          component={Grid}
          container
          item
          display="flex"
          direction="row"
          sx={{
            borderRadius: 3,
            flexShrink: 0,
            display: isEditSelected ? 'flex' : 'none',
          }}
        >
          <Grid container direction="column" spacing={2}>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={addEventValidation}
              onSubmit={onUpdateEvent}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                    <Alert mt={2} mb={3} severity={notification?.type}>
                      {notification?.message}
                    </Alert>
                  )}
                  <TextField
                    label=" Date"
                    type="date"
                    fullWidth
                    name="date"
                    value={values.date}
                    error={Boolean(touched.date && errors.date)}
                    helperText={touched.date && errors.date}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    sx={{ borderRadius: 5 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="filled"
                    margin="normal"
                  />
                  {majorEventCategories && (
                    <TextField
                      select
                      fullWidth
                      name="majorCategory"
                      value={values.majorCategory}
                      disabled
                      error={Boolean(touched.majorCategory && errors.majorCategory)}
                      helperText={touched.majorCategory && errors.majorCategory}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      label="Major Category"
                      variant="filled"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                      }}
                    >
                      {majorEventCategories?.map((type) => (
                        <MenuItem key={type?.id} value={type?.name}>
                          {type?.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {minorEventCategories.length > 0 && minorEventCategories && (
                    <TextField
                      select
                      fullWidth
                      name="minorCategory"
                      disabled={isEmpty(values.majorCategory)}
                      value={values.minorCategory}
                      error={Boolean(touched.minorCategory && errors.minorCategory)}
                      helperText={touched.minorCategory && errors.minorCategory}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      label="Minor Category"
                      variant="filled"
                      margin="normal"
                      InputProps={{
                        disableUnderline: true,
                      }}
                    >
                      {minorEventCategories?.map((type) => (
                        <MenuItem key={type?.id} value={type?.name}>
                          {type?.name}
                        </MenuItem>
                      )) ?? <MenuItem />}
                    </TextField>
                  )}
                  <TextField
                    label=" Description"
                    variant="filled"
                    name="description"
                    fullWidth
                    value={values.description}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    margin="normal"
                    InputProps={{
                      disableUnderline: true,
                    }}
                  />
                  <Grid item container direction="row" justifyContent="space-between" my={2}>
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      Save Changes
                    </Button>
                    <Button variant="outlined" onClick={() => setEditSelected(false)}>
                      Cancel
                    </Button>
                  </Grid>
                  <input
                    ref={formElement}
                    style={{
                      visibility: 'hidden',
                    }}
                    type="submit"
                  />
                </form>
              )}
            </Formik>
          </Grid>
        </Box>
      </Grid>
    </Loader>
  );
};
//
export default UpdateEventForm;
